import React, { useEffect, useState, useRef } from 'react';
import { X, Send, Loader2, Minus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { chatGPT } from '../../utils/api';
interface ChatWidgetProps {
  language: 'ar' | 'en';
}
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/faWT17kE43WdqrZMBmZAS3/Operines-Icon-color1_(2).png';
// Animated AI Brain Icon
function AIIcon({
  size = 22,
  color = 'white'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-530">
      {/* Brain/circuit AI icon */}
      <circle cx="12" cy="12" r="3" fill={color} opacity="0.9" data-id="element-531" />
      <circle cx="12" cy="4" r="1.5" fill={color} opacity="0.7" data-id="element-532" />
      <circle cx="12" cy="20" r="1.5" fill={color} opacity="0.7" data-id="element-533" />
      <circle cx="4" cy="12" r="1.5" fill={color} opacity="0.7" data-id="element-534" />
      <circle cx="20" cy="12" r="1.5" fill={color} opacity="0.7" data-id="element-535" />
      <circle cx="6.34" cy="6.34" r="1.5" fill={color} opacity="0.6" data-id="element-536" />
      <circle cx="17.66" cy="17.66" r="1.5" fill={color} opacity="0.6" data-id="element-537" />
      <circle cx="17.66" cy="6.34" r="1.5" fill={color} opacity="0.6" data-id="element-538" />
      <circle cx="6.34" cy="17.66" r="1.5" fill={color} opacity="0.6" data-id="element-539" />
      <line x1="12" y1="9" x2="12" y2="5.5" stroke={color} strokeWidth="1.2" opacity="0.5" data-id="element-540" />
      <line x1="12" y1="15" x2="12" y2="18.5" stroke={color} strokeWidth="1.2" opacity="0.5" data-id="element-541" />
      <line x1="9" y1="12" x2="5.5" y2="12" stroke={color} strokeWidth="1.2" opacity="0.5" data-id="element-542" />
      <line x1="15" y1="12" x2="18.5" y2="12" stroke={color} strokeWidth="1.2" opacity="0.5" data-id="element-543" />
      <line x1="9.76" y1="9.76" x2="7.4" y2="7.4" stroke={color} strokeWidth="1.2" opacity="0.4" data-id="element-544" />
      <line x1="14.24" y1="14.24" x2="16.6" y2="16.6" stroke={color} strokeWidth="1.2" opacity="0.4" data-id="element-545" />
      <line x1="14.24" y1="9.76" x2="16.6" y2="7.4" stroke={color} strokeWidth="1.2" opacity="0.4" data-id="element-546" />
      <line x1="9.76" y1="14.24" x2="7.4" y2="16.6" stroke={color} strokeWidth="1.2" opacity="0.4" data-id="element-547" />
    </svg>;
}
export function ChatWidget({
  language
}: ChatWidgetProps) {
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{
    role: 'user' | 'bot';
    message: string;
  }>>([]);
  const [hasUnread, setHasUnread] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chatHistory, isLoading]);
  useEffect(() => {
    if (isOpen && chatHistory.length === 0) {
      setChatHistory([{
        role: 'bot',
        message: isRTL ? 'مرحباً! 👋 أنا مساعد Operines الذكي. كيف يمكنني مساعدتك اليوم؟' : "Hello! 👋 I'm Operines AI assistant. How can I help you today?"
      }]);
    }
  }, [isOpen]);
  const handleSend = async () => {
    if (!message.trim() || isLoading) return;
    const userMsg = message.trim();
    setMessage('');
    setChatHistory(prev => [...prev, {
      role: 'user',
      message: userMsg
    }]);
    setIsLoading(true);
    try {
      const response = await chatGPT.sendMessage(userMsg, language);
      setChatHistory(prev => [...prev, {
        role: 'bot',
        message: response
      }]);
    } catch {
      setChatHistory(prev => [...prev, {
        role: 'bot',
        message: isRTL ? 'عذراً، حدث خطأ. حاول مرة أخرى.' : 'Sorry, an error occurred. Try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  return <>
      {/* Chat Panel */}
      <AnimatePresence data-id="element-548">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} className="fixed bottom-24 z-[100] w-[360px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col" style={{
        right: isRTL ? 'auto' : '20px',
        left: isRTL ? '20px' : 'auto',
        height: '480px'
      }} data-id="element-549">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#4f1964] to-purple-600 px-4 py-3 flex items-center justify-between flex-shrink-0" data-id="element-550">
              <div className="flex items-center gap-3" data-id="element-551">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center border border-white/30" data-id="element-552">
                  <AIIcon size={18} color="white" data-id="element-553" />
                </div>
                <div data-id="element-554">
                  <h3 className="text-white font-semibold text-sm" data-id="element-555">
                    Operines AI
                  </h3>
                  <div className="flex items-center gap-1" data-id="element-556">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" data-id="element-557" />
                    <span className="text-white/70 text-[11px]" data-id="element-558">
                      {isRTL ? 'متصل' : 'Online'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1" data-id="element-559">
                <button onClick={() => setIsOpen(false)} className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-560">
                  <Minus className="w-4 h-4" data-id="element-561" />
                </button>
                <button onClick={() => {
              setIsOpen(false);
              setChatHistory([]);
            }} className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-562">
                  <X className="w-4 h-4" data-id="element-563" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-564">
              {chatHistory.map((chat, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.05
          }} className={`flex ${chat.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-565">
                  {chat.role === 'bot' && <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#4f1964] to-purple-500 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5" data-id="element-566">
                      <AIIcon size={13} color="white" data-id="element-567" />
                    </div>}
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${chat.role === 'user' ? 'bg-[#4f1964] text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'}`} data-id="element-568">
                    <p className="whitespace-pre-wrap" data-id="element-569">{chat.message}</p>
                  </div>
                </motion.div>)}
              {isLoading && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex justify-start" data-id="element-570">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#4f1964] to-purple-500 flex items-center justify-center flex-shrink-0 mr-2" data-id="element-571">
                    <AIIcon size={13} color="white" data-id="element-572" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm" data-id="element-573">
                    <div className="flex items-center gap-1" data-id="element-574">
                      {[0, 0.15, 0.3].map((delay, i) => <motion.div key={i} animate={{
                  scale: [1, 1.3, 1]
                }} transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay
                }} className="w-1.5 h-1.5 bg-[#4f1964] rounded-full" data-id="element-575" />)}
                    </div>
                  </div>
                </motion.div>}
              <div ref={messagesEndRef} data-id="element-576" />
            </div>

            {/* Input */}
            <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-577">
              <div className="flex gap-2" data-id="element-578">
                <input type="text" value={message} onChange={e => setMessage(e.target.value)} placeholder={isRTL ? 'اكتب رسالة...' : 'Type a message...'} className="flex-1 bg-gray-100 border-0 rounded-full px-4 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-[#4f1964] focus:outline-none" onKeyDown={e => e.key === 'Enter' && handleSend()} disabled={isLoading} data-id="element-579" />
                <button onClick={handleSend} disabled={isLoading || !message.trim()} className="w-10 h-10 bg-[#4f1964] hover:bg-[#3d1350] rounded-full flex items-center justify-center text-white disabled:opacity-40 transition-colors flex-shrink-0" data-id="element-580">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-581" /> : <Send className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} data-id="element-582" />}
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button onClick={() => {
      setIsOpen(!isOpen);
      setHasUnread(false);
    }} className="fixed bottom-6 z-[100] w-12 h-12 rounded-full shadow-xl flex items-center justify-center border-2 border-white/30" style={{
      right: isRTL ? 'auto' : '20px',
      left: isRTL ? '20px' : 'auto',
      background: 'linear-gradient(135deg, #4f1964, #7c3aed)'
    }} whileHover={{
      scale: 1.1
    }} whileTap={{
      scale: 0.9
    }} data-id="element-583">
        <AnimatePresence mode="wait" data-id="element-584">
          {isOpen ? <motion.div key="close" initial={{
          rotate: -90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: 90,
          opacity: 0
        }} transition={{
          duration: 0.15
        }} data-id="element-585">
              <X className="w-5 h-5 text-white" data-id="element-586" />
            </motion.div> : <motion.div key="ai" initial={{
          rotate: 90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: -90,
          opacity: 0
        }} transition={{
          duration: 0.15
        }} data-id="element-587">
              {/* Rotating AI icon animation */}
              <motion.div animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-588">
                <AIIcon size={20} color="white" data-id="element-589" />
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Unread indicator */}
        {!isOpen && hasUnread && <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white" data-id="element-590" />}

        {/* Pulse ring */}
        {!isOpen && <motion.div className="absolute inset-0 rounded-full border-2 border-purple-400" animate={{
        scale: [1, 1.5],
        opacity: [0.5, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} data-id="element-591" />}
      </motion.button>
    </>;
}